import { useEffect, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { backdrop, panel, tap } from "../motion";
import { IconX, IconChevronLeft, IconChevronRight } from "./icons";
import { icpStatusLabel } from "../hooks/useResidents";
import { useIcpActions, useResidentDetail, type ResidentDetail } from "../hooks/useIcpFlow";
import { fmtCell, MONTHS, monthCells, WEEKDAYS } from "./calendar-grid";
import { advanceIcpFrequency, hkToday, nextIcpDeadline } from "../lib/dates";

// Guided Ax/ICP completion — one ordered walkthrough of the fill state machine
// (design-notes.md): Ax -> wait for the printed form -> fill + close the loop.
// Server transitions go through POST /icp/complete (atomic); the intermediate
// "form available" step is the plain resident PATCH. External work (print,
// Sheet 007, paper form) is a checklist with links, not a gate.

type StepState = "done" | "current" | "upcoming";

function Step({ n, state, title, children }: { n: number; state: StepState; title: string; children?: ReactNode }) {
  return (
    <li className={`flow-step ${state}`}>
      <span className={`flow-dot ${state}`} aria-hidden>
        {state === "done" ? (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
        ) : (
          n
        )}
      </span>
      <div className="flow-step-main">
        <p className="flow-step-title">{title}</p>
        {children}
      </div>
    </li>
  );
}

function FlowBody({ detail, onClose }: { detail: ResidentDetail; onClose: () => void }) {
  const { patch, complete } = useIcpActions(detail.id);
  const status = detail.icpFillStatus ?? "pending_ax";
  const [closeTodos, setCloseTodos] = useState(true);
  // The staff-written ICP date anchors the next deadline (it varies and cannot
  // be derived from the fill date) — required, no default.
  const [icpDate, setIcpDate] = useState("");
  const [calView, setCalView] = useState(() => {
    const [y, m] = hkToday().split("-").map(Number);
    return { year: y!, month: m! - 1 };
  });
  const cells = monthCells(calView.year, calView.month);
  const calToday = hkToday();
  const shiftMonth = (delta: number) =>
    setCalView((v) => {
      const month = v.month + delta;
      if (month < 0) return { year: v.year - 1, month: 11 };
      if (month > 11) return { year: v.year + 1, month: 0 };
      return { ...v, month };
    });
  const pickIcpDate = (d: string) => {
    setIcpDate(d);
    const [y, m] = d.split("-").map(Number);
    setCalView({ year: y!, month: m! - 1 });
  };


  const axState: StepState = status === "pending_ax" ? "current" : "done";
  const formState: StepState = status === "pending_ax" ? "upcoming" : status === "ax_done_awaiting_form" ? "current" : "done";
  const fillState: StepState = status === "filled" ? "done" : status === "form_available" ? "current" : "upcoming";
  const busy = patch.isPending || complete.isPending;

  return (
    <>
      <div className="modal-body">
        <div className="todo-meta">
          {detail.nextIcpDate ? <span className={`chip ${detail.overdue ? "chip-danger" : detail.dueSoon ? "chip-warn" : ""}`}>ICP {detail.nextIcpDate}</span> : null}
          {detail.icpFillStatus ? <span className={`chip ${status === "filled" ? "chip-ok" : status === "form_available" ? "chip-accent" : "chip-warn"}`}>{icpStatusLabel(detail.icpFillStatus)}</span> : null}
          {detail.workWindowToday ? <span className="chip chip-accent">Do this half-month</span> : null}
        </div>

        <ol className="flow-steps">
          <Step n={1} state={axState} title="Do the assessment (Ax)">
            {axState === "current" ? (
              <>
                <p className="muted">Do the Ax inside this half-month's work window — it counts toward the ICP.</p>
                <motion.button type="button" className="btn btn-primary" {...tap} disabled={busy} onClick={() => complete.mutate({ stage: "ax" })}>
                  {complete.isPending ? "Saving…" : "Mark Ax done"}
                </motion.button>
              </>
            ) : (
              <p className="muted">{detail.lastAxDate ? `Ax done ${detail.lastAxDate}` : "Ax recorded"}</p>
            )}
          </Step>

          <Step n={2} state={formState} title="Wait for the home to print the ICP form">
            {formState === "current" ? (
              <>
                <p className="muted">Home staff print the ICP form (often late). Tick when the form is ready.</p>
                <motion.button type="button" className="btn btn-secondary" {...tap} disabled={busy} onClick={() => patch.mutate({ icpFillStatus: "form_available" })}>
                  {patch.isPending ? "Saving…" : "Form available"}
                </motion.button>
              </>
            ) : null}
          </Step>

          <Step n={3} state={fillState} title="Fill the ICP and close the loop">
            {fillState === "current" ? (
              <>
                                <div className="todo-check-label">
                  <button
                    type="button"
                    className={`todo-check ${closeTodos ? "done" : ""}`}
                    aria-pressed={closeTodos}
                    aria-label="Complete this resident's Ax/ICP to-dos"
                    onClick={() => setCloseTodos((v) => !v)}
                  >
                    {closeTodos ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M20 6 9 17l-5-5" /></svg>
                    ) : null}
                  </button>
                  <span>Complete this resident's Ax/ICP to-dos</span>
                </div>
                <label htmlFor="icp-fill-date">ICP date (as written by staff)</label>
                <div className="mini-cal is-inline" role="group" aria-label="ICP date">
                  <div className="mini-cal-head">
                    <button type="button" className="mini-cal-nav" aria-label="Previous month" onClick={() => shiftMonth(-1)}>
                      <IconChevronLeft />
                    </button>
                    <div className="mini-cal-title" aria-live="polite">
                      {MONTHS[calView.month]} {calView.year}
                    </div>
                    <button type="button" className="mini-cal-nav" aria-label="Next month" onClick={() => shiftMonth(1)}>
                      <IconChevronRight />
                    </button>
                  </div>
                  <div className="mini-cal-grid">
                    {WEEKDAYS.map((w) => (
                      <div key={w} className="mini-cal-dow">
                        {w}
                      </div>
                    ))}
                    {cells.map((c) => (
                      <button
                        key={c.key}
                        type="button"
                        className={`mini-cal-day${c.out ? " out" : ""}${c.date === icpDate ? " selected" : ""}${c.date === calToday ? " today" : ""}`}
                        aria-label={fmtCell(c.date)}
                        aria-pressed={c.date === icpDate}
                        onClick={() => pickIcpDate(c.date)}
                      >
                        {c.day}
                      </button>
                    ))}
                  </div>
                  <div className="mini-cal-foot">
                    <button type="button" className="mini-cal-today" onClick={() => pickIcpDate(calToday)}>
                      Today
                    </button>
                  </div>
                </div>
                {icpDate ? (
                  <p className="muted">Next ICP {nextIcpDeadline(icpDate, advanceIcpFrequency(detail.icpFrequency))}</p>
                ) : null}
                {detail.deepLink ? (
                  <p className="muted">
                    Also update the sheet (007 住客資料 + ICP S/T/U): <a href={detail.deepLink} target="_blank" rel="noopener noreferrer">open the row →</a>
                  </p>
                ) : (
                  <p className="muted">Also update Sheet 007 (住客資料 + ICP S/T/U).</p>
                )}
                <motion.button
                  type="button"
                  className="btn btn-primary"
                  {...tap}
                  disabled={busy || icpDate.length !== 10}
                  onClick={() => complete.mutate({ stage: "fill", completeLinkedTodos: closeTodos, icpDate })}
                >
                  {complete.isPending ? "Saving…" : "Mark ICP filled"}
                </motion.button>
              </>
            ) : fillState === "done" ? (
              <p className="muted">{detail.nextIcpDate ? `Next ICP ${detail.nextIcpDate}` : "Cycle complete"} · still update Sheet 007 if not done yet.</p>
            ) : (
              <p className="muted">Unlocks once the form is available.</p>
            )}
          </Step>
        </ol>

        {patch.isError || complete.isError ? <p className="error">The step failed — nothing was half-applied. Try again.</p> : null}
      </div>
      <div className="modal-foot">
        <button type="button" className="btn btn-ghost" onClick={onClose}>Close</button>
      </div>
    </>
  );
}

export function GuidedIcpFlow({ residentId, onClose }: { residentId: string | null; onClose: () => void }) {
  const detail = useResidentDetail(residentId);

  useEffect(() => {
    if (!residentId) return;
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [residentId, onClose]);

  return (
    <AnimatePresence>
      {residentId ? (
        <motion.div className="palette-backdrop" role="dialog" aria-modal="true" aria-label="Guided Ax/ICP completion" onClick={onClose} variants={backdrop} initial="hidden" animate="show" exit="exit">
          <motion.div className="modal" onClick={(e) => e.stopPropagation()} variants={panel} initial="hidden" animate="show" exit="exit">
            <div className="modal-head">
              <h2>{detail.data ? detail.data.name : "ICP completion"}</h2>
              {detail.data?.bed ? <span className="chip">{detail.data.bed}</span> : null}
              {detail.data?.homeName ? <span className="chip">{detail.data.homeName}</span> : null}
              <button type="button" className="btn btn-icon btn-ghost" aria-label="Close" onClick={onClose}><IconX /></button>
            </div>
            {detail.isLoading ? <p className="muted" style={{ padding: "var(--space-4)" }}>Loading…</p> : null}
            {detail.isError ? <p className="error" style={{ padding: "var(--space-4)" }}>Could not load this resident. Try again.</p> : null}
            {detail.data ? <FlowBody key={detail.data.id} detail={detail.data} onClose={onClose} /> : null}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
